import {
  useMutation,
  useQueryClient,
  type QueryKey,
  type UseMutationOptions,
} from '@tanstack/react-query';

import { useApiFeedback } from '@/hooks/useApiFeedback';

type FeedbackKey<TData, TVariables> =
  string | ((data: TData, variables: TVariables) => string);

type UseApiMutationOptions<
  TData,
  TVariables,
  TError,
  TOnMutateResult = unknown,
> = Omit<
  UseMutationOptions<TData, TError, TVariables, TOnMutateResult>,
  'mutationFn'
> & {
  mutationFn: (variables: TVariables) => Promise<TData>;
  successKey?: FeedbackKey<TData, TVariables>;
  errorKey?: FeedbackKey<TData, TVariables>;
  successDuration?: number;
  silent?: boolean;
  invalidateKeys?: QueryKey[];
};

function resolveFeedbackKey<TData, TVariables>(
  key: FeedbackKey<TData, TVariables> | undefined,
  data: TData,
  variables: TVariables,
): string | undefined {
  if (key == null) {
    return undefined;
  }
  return typeof key === 'function' ? key(data, variables) : key;
}

export function useApiMutation<
  TData,
  TVariables = void,
  TError = Error,
  TOnMutateResult = unknown,
>(options: UseApiMutationOptions<TData, TVariables, TError, TOnMutateResult>) {
  const { showSuccess, showErrorFrom } = useApiFeedback();
  const queryClient = useQueryClient();
  const {
    mutationFn,
    successKey,
    errorKey,
    successDuration,
    silent,
    invalidateKeys,
    onSuccess,
    onError,
    ...rest
  } = options;

  const mutation = useMutation<TData, TError, TVariables, TOnMutateResult>({
    mutationFn,
    onSuccess: (data, variables, onMutateResult, context) => {
      const resolvedSuccessKey = resolveFeedbackKey(
        successKey,
        data,
        variables,
      );
      if (resolvedSuccessKey && !silent) {
        showSuccess(resolvedSuccessKey, successDuration);
      }
      invalidateKeys?.forEach((key) => {
        void queryClient.invalidateQueries({ queryKey: key });
      });
      onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      if (!silent) {
        const resolvedErrorKey = resolveFeedbackKey(
          errorKey,
          undefined as TData,
          variables,
        );
        showErrorFrom(error, resolvedErrorKey);
      }
      onError?.(error, variables, onMutateResult, context);
    },
    ...rest,
  });

  return {
    ...mutation,
    loading: mutation.isPending,
  };
}
